import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { usePagesStore } from '../stores/pagesStore';
import { useDialogStore } from '../stores/dialogStore';
import { useWidgetsStore } from '../stores/widgetsStore';
import { useLayoutsStore } from '../stores/layoutsStore';
import { useGalleriesStore } from '../stores/galleriesStore';
import ProtectedImage from './ProtectedImage';
import ProtectedVideo from './ProtectedVideo';
import ProtectedVideoThumbnail from './ProtectedVideoThumbnail';
import RichTextEditorExtended from './RichTextEditorExtended';
import GalleryRenderer from './GalleryRenderer';
import ProfileDetails from './widgets/ProfileDetails';
import GalleriesWidget from './widgets/GalleriesWidget';
import NewestMembers from './widgets/NewestMembers';
import CalendarWidget from './widgets/CalendarWidget';
import NewsBulletinWidget from './widgets/NewsBulletinWidget';
import Layouts from '../pages/admin/Layouts';
import Lookups from '../pages/admin/Lookups';
import Widgets from '../pages/admin/Widgets';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Widget mapping - maps widget codes to components
const WIDGET_MAP = {
  'PROFILE_DETAILS': ProfileDetails,
  'GALLERIES': GalleriesWidget,
  'NEWEST_MEMBERS': NewestMembers,
  'CALENDAR': CalendarWidget,
  'NEWS_BULLETIN': NewsBulletinWidget,
  'LAYOUTS': Layouts,
  'LOOKUPS': Lookups,
  'WIDGETS': Widgets
};

// Sortable Paragraph Item Component
const SortableParagraph = ({
  paragraph,
  depth = 0,
  onEdit,
  onDelete,
  onAddChild,
  editingId,
  editValues,
  setEditValues,
  onSave,
  onCancelEdit,
  onGetWidgets,
  onGetWidgetLayouts,
  onGetMediaLayouts,
  onGetGalleryLayouts,
  onCreateGallery,
  onRemoveGallery,
  onRemoveWidget,
  onAddWidget,
  onMediaLayoutChange,
  onWidgetLayoutChange,
  onWidgetPropsChange,
  onAddMediaToGallery,
  onDeleteMediaFromGallery,
  onReorderMedia,
  onViewMedia,
  onGalleryLayoutChange,
  localGalleryMedia,
  galleryLayouts,
  setGalleryLayouts,
  uploadingGalleryId,
  profile,
  bulletinContexts
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: paragraph.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isEditing = editingId === paragraph.id;
  const maxDepth = 3;
  const canAddChild = depth < maxDepth - 1;

  // Helper to render child paragraphs
  const renderChildren = () => {
    if (!paragraph.children || paragraph.children.length === 0) return null;
    return (
      <div className="ml-6 mt-2">
        {paragraph.children.map((child) => (
          <SortableParagraph
            key={child.id}
            paragraph={child}
            depth={depth + 1}
            onEdit={onEdit}
            onDelete={onDelete}
            onAddChild={onAddChild}
            editingId={editingId}
            editValues={editValues}
            setEditValues={setEditValues}
            onSave={onSave}
            onCancelEdit={onCancelEdit}
            onGetWidgets={onGetWidgets}
            onGetWidgetLayouts={onGetWidgetLayouts}
            onGetMediaLayouts={onGetMediaLayouts}
            onGetGalleryLayouts={onGetGalleryLayouts}
            onCreateGallery={onCreateGallery}
            onRemoveGallery={onRemoveGallery}
            onRemoveWidget={onRemoveWidget}
            onAddWidget={onAddWidget}
            onMediaLayoutChange={onMediaLayoutChange}
            onWidgetLayoutChange={onWidgetLayoutChange}
            onWidgetPropsChange={onWidgetPropsChange}
            onAddMediaToGallery={onAddMediaToGallery}
            onDeleteMediaFromGallery={onDeleteMediaFromGallery}
            onReorderMedia={onReorderMedia}
            onViewMedia={onViewMedia}
            onGalleryLayoutChange={onGalleryLayoutChange}
            localGalleryMedia={localGalleryMedia}
            galleryLayouts={galleryLayouts}
            setGalleryLayouts={setGalleryLayouts}
            uploadingGalleryId={uploadingGalleryId}
            profile={profile}
            bulletinContexts={bulletinContexts}
          />
        ))}
      </div>
    );
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className="mb-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
      >
        {isEditing ? (
          // Edit Mode
          <div className="p-4">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-secondary mb-1">
                  Content
                </label>
                <RichTextEditorExtended
                  value={editValues.content || ''}
                  onChange={(html) => setEditValues({ ...editValues, content: html })}
                  placeholder="Enter paragraph content..."
                />
              </div>

              {/* Note: Widget and Media management moved to title bar */}
              <div className="text-xs text-gray-600 dark:text-gray-400 italic p-3 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                Use the title bar buttons to add media or widgets to this paragraph.
              </div>

              <div className="hidden border border-gray-300 dark:border-gray-600 rounded p-3">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-secondary">
                    Content Type (choose one)
                  </label>
                  {editValues.widget_id && (
                    <button
                      type="button"
                      onClick={() => setEditValues({ ...editValues, widget_id: '', widget_layout_id: '' })}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Clear Widget
                    </button>
                  )}
                </div>

                {/* Show Widget Section if no gallery - only for top-level paragraphs */}
                {depth === 0 && !paragraph.media_gallery_id && (
                  <div className="mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-secondary mb-1">
                          Widget
                        </label>
                        <select
                          value={editValues.widget_id || ''}
                          onChange={(e) => setEditValues({ ...editValues, widget_id: e.target.value || null })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary text-sm"
                        >
                          <option value="">None</option>
                          {onGetWidgets && onGetWidgets().map((widget) => (
                            <option key={widget.id} value={widget.id}>
                              {widget.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs text-secondary mb-1">
                          Widget Layout
                        </label>
                        <select
                          value={editValues.widget_layout_id || ''}
                          onChange={(e) => setEditValues({ ...editValues, widget_layout_id: e.target.value || null })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary text-sm"
                          disabled={!editValues.widget_id}
                        >
                          <option value="">Default</option>
                          {onGetWidgetLayouts && onGetWidgetLayouts().map((layout) => (
                            <option key={layout.id} value={layout.id}>
                              {layout.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Show Media Section if no widget - only for top-level paragraphs */}
                {depth === 0 && !editValues.widget_id && (
                  <div>
                    <label className="block text-xs text-secondary mb-2">
                      Media Gallery
                    </label>

                    {paragraph.media_gallery_id ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-secondary">Media Gallery</span>
                      <button
                        type="button"
                        onClick={() => onRemoveGallery(paragraph.id)}
                        className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete Gallery"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>

                    <div>
                      <label className="block text-xs text-secondary mb-1">Paragraph Layout (text flow)</label>
                      <select
                        value={editValues.media_layout_id || ''}
                        onChange={(e) => setEditValues({ ...editValues, media_layout_id: e.target.value || null })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary text-sm"
                      >
                        <option value="">Default</option>
                        {onGetMediaLayouts && onGetMediaLayouts().map((layout) => (
                          <option key={layout.id} value={layout.id}>
                            {layout.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs text-secondary mb-1">Gallery Layout (item display)</label>
                      <select
                        value={String(galleryLayouts[paragraph.media_gallery_id]?.layout_id || '')}
                        onChange={(e) => {
                          const layoutId = e.target.value;
                          if (!layoutId) return;

                          const layout = onGetGalleryLayouts().find(l => l.id === parseInt(layoutId));
                          if (layout) {
                            // Optimistically update local state first for immediate UI feedback
                            setGalleryLayouts(prev => ({
                              ...prev,
                              [paragraph.media_gallery_id]: {
                                layout_code: layout.code,
                                layout_id: layout.id
                              }
                            }));
                            // Then update backend
                            onGalleryLayoutChange(paragraph.media_gallery_id, layout.code);
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary text-sm"
                      >
                        <option value="">Default</option>
                        {onGetGalleryLayouts && onGetGalleryLayouts().map((layout) => (
                          <option key={layout.id} value={String(layout.id)}>
                            {layout.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Media Grid */}
                    {localGalleryMedia[paragraph.media_gallery_id] && localGalleryMedia[paragraph.media_gallery_id].length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        {localGalleryMedia[paragraph.media_gallery_id].map((media) => (
                          <div
                            key={media.id}
                            className="relative aspect-square bg-gray-100 dark:bg-gray-700 rounded overflow-hidden group"
                          >
                            <div
                              className="w-full h-full cursor-pointer"
                              onClick={() => onViewMedia(media)}
                            >
                              {media.kind_code === 'IMAGE' ? (
                                <ProtectedImage
                                  uuid={media.thumbnail_uuid || media.uuid}
                                  alt={media.filename}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <ProtectedVideoThumbnail
                                  uuid={media.uuid}
                                  className="w-full h-full object-cover"
                                />
                              )}
                            </div>
                            <button
                              onClick={() => onDeleteMediaFromGallery(paragraph.media_gallery_id, media.id)}
                              className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                              title="Delete"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Upload Button / Progress */}
                    {uploadingGalleryId === paragraph.media_gallery_id ? (
                      <div className="mt-2">
                        <div className="text-sm text-secondary mb-2">Uploading...</div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div className="bg-primary-600 h-2 rounded-full transition-all animate-pulse" style={{ width: '100%' }}></div>
                        </div>
                      </div>
                    ) : (
                      <label className="block w-full mt-2">
                        <input
                          type="file"
                          multiple
                          accept="image/*,video/*"
                          onChange={(e) => {
                            const files = Array.from(e.target.files);
                            if (files.length > 0) {
                              onAddMediaToGallery(paragraph.media_gallery_id, files);
                              e.target.value = '';
                            }
                          }}
                          className="hidden"
                        />
                        <div className="px-3 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded text-center text-secondary hover:border-primary-600 hover:text-primary-600 text-sm cursor-pointer">
                          + Add Media
                        </div>
                      </label>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => onCreateGallery(paragraph.id)}
                    className="w-full px-3 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded text-secondary hover:border-primary-600 hover:text-primary-600 text-sm"
                  >
                    + Create Media Gallery
                  </button>
                )}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => onSave(paragraph.id)}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Save
                </button>
                <button
                  onClick={onCancelEdit}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-secondary hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : (
          // View Mode - WYSIWYG rendering with Title Bar
          <div>
            {/* Title Bar */}
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              {/* Left: Drag Handle + Conditional Content */}
              <div className="flex items-center gap-2">
                <div
                  {...attributes}
                  {...listeners}
                  className="cursor-move text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  title="Drag to reorder"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                  </svg>
                </div>

                {!paragraph.media_gallery_id && !paragraph.widget_id ? (
                  // Show Add Media and Add Widget only for top-level paragraphs
                  depth === 0 ? (
                    <>
                      <button
                        onClick={() => onCreateGallery(paragraph.id)}
                        className="px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
                        title="Add Media"
                      >
                        + Media
                      </button>
                      <select
                        value=""
                        onChange={(e) => onAddWidget(paragraph.id, e.target.value)}
                        className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary cursor-pointer"
                      >
                        <option value="">+ Widget</option>
                        {onGetWidgets && onGetWidgets().filter(w => w.is_active).map((widget) => (
                          <option key={widget.id} value={widget.id}>
                            {widget.label}
                          </option>
                        ))}
                      </select>
                    </>
                  ) : null
                ) : paragraph.media_gallery_id ? (
                  // Show Media Layout dropdown when media exists
                  <select
                    value={paragraph.media_layout_id || ''}
                    onChange={(e) => onMediaLayoutChange(paragraph.id, e.target.value || null)}
                    className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
                  >
                    <option value="">Media Layout</option>
                    {onGetMediaLayouts && onGetMediaLayouts().map((layout) => (
                      <option key={layout.id} value={layout.id}>
                        {layout.label}
                      </option>
                    ))}
                  </select>
                ) : paragraph.widget_id ? (
                  // Show Widget Layout dropdown when widget exists
                  <>
                    <select
                      value={paragraph.widget_layout_id || ''}
                      onChange={(e) => onWidgetLayoutChange(paragraph.id, e.target.value || null)}
                      className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
                    >
                      <option value="">Widget Layout</option>
                      {onGetWidgetLayouts && onGetWidgetLayouts().map((layout) => (
                        <option key={layout.id} value={layout.id}>
                          {layout.label}
                        </option>
                      ))}
                    </select>

                    {/* Show Context selector for NEWS_BULLETIN widget */}
                    {paragraph.widget_code === 'NEWS_BULLETIN' && (() => {
                      // Parse widget_props if it's a string
                      let props = {};
                      if (paragraph.widget_props) {
                        try {
                          props = typeof paragraph.widget_props === 'string'
                            ? JSON.parse(paragraph.widget_props)
                            : paragraph.widget_props;
                        } catch (e) {
                          console.error('Failed to parse widget_props:', e);
                        }
                      }

                      return (
                        <select
                          value={props.context_id || ''}
                          onChange={(e) => {
                            const contextId = parseInt(e.target.value);
                            const newProps = { ...props, context_id: contextId };
                            onWidgetPropsChange(paragraph.id, newProps);
                          }}
                          className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
                        >
                          <option value="">Bulletin Context</option>
                          {bulletinContexts.map((context) => (
                            <option key={context.id} value={context.id}>
                              {context.label}
                            </option>
                          ))}
                        </select>
                      );
                    })()}
                  </>
                ) : null}
              </div>

              {/* Right: Action Buttons */}
              <div className="flex items-center gap-1">
                {canAddChild && (
                  <button
                    onClick={() => onAddChild(paragraph.id)}
                    className="p-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                    title="Add child paragraph"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => onEdit(paragraph)}
                  className="p-1 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  title="Edit content"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => onDelete(paragraph.id)}
                  className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  title="Delete paragraph"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Paragraph Content with WYSIWYG rendering */}
            <div className="p-4">
              {(() => {
                return (
                  <>
                    {/* Widget Rendering with Layout */}
              {paragraph.widget_id && paragraph.widget_code && (() => {
                const WidgetComponent = WIDGET_MAP[paragraph.widget_code];
                if (!WidgetComponent) {
                  return (
                    <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 mb-4">
                      <p className="text-xs text-secondary">Widget not implemented: {paragraph.widget_code}</p>
                    </div>
                  );
                }

                const widgetProps = paragraph.widget_props ? JSON.parse(paragraph.widget_props) : {};
                const widgetLayoutCode = paragraph.widget_layout_code;
                const widgetElement = (
                  <div className="relative">
                    <button
                      onClick={() => onRemoveWidget(paragraph.id)}
                      className="absolute top-2 right-2 z-10 p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 bg-white dark:bg-gray-800 rounded-full shadow-md"
                      title="Remove widget"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                    <WidgetComponent profileId={profile?.id} {...widgetProps} />
                  </div>
                );

                // Render with layout
                if (widgetLayoutCode === 'WIDGET_CLEAR_LEFT') {
                  return (
                    <>
                      <div className="flex flex-col md:flex-row gap-4 mb-4">
                        <div className="md:w-1/3">{widgetElement}</div>
                        <div className="md:w-2/3">
                          {paragraph.content && (
                            <div className="text-secondary prose dark:prose-invert paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                          )}
                          {renderChildren()}
                        </div>
                      </div>
                    </>
                  );
                } else if (widgetLayoutCode === 'WIDGET_CLEAR_RIGHT') {
                  return (
                    <>
                      <div className="flex flex-col md:flex-row gap-4 mb-4">
                        <div className="md:w-2/3">
                          {paragraph.content && (
                            <div className="text-secondary prose dark:prose-invert paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                          )}
                          {renderChildren()}
                        </div>
                        <div className="md:w-1/3">{widgetElement}</div>
                      </div>
                    </>
                  );
                } else if (widgetLayoutCode === 'WIDGET_FLOAT_LEFT') {
                  return (
                    <>
                      <div className="float-left mr-4 mb-4 w-full md:w-1/3">{widgetElement}</div>
                      {paragraph.content && (
                        <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                      )}
                      {renderChildren()}
                      <div className="clear-both"></div>
                    </>
                  );
                } else if (widgetLayoutCode === 'WIDGET_FLOAT_RIGHT') {
                  return (
                    <>
                      <div className="float-right ml-4 mb-4 w-full md:w-1/3">{widgetElement}</div>
                      {paragraph.content && (
                        <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                      )}
                      {renderChildren()}
                      <div className="clear-both"></div>
                    </>
                  );
                } else if (widgetLayoutCode === 'WIDGET_ABOVE_PARAGRAPH') {
                  return (
                    <>
                      {widgetElement}
                      {paragraph.content && (
                        <div className="mt-4 text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                      )}
                      {renderChildren()}
                    </>
                  );
                } else if (widgetLayoutCode === 'WIDGET_BELOW_PARAGRAPH') {
                  return (
                    <>
                      {paragraph.content && (
                        <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                      )}
                      {renderChildren()}
                      <div className="mt-4">{widgetElement}</div>
                    </>
                  );
                } else {
                  // No layout - just show widget and content + children
                  return (
                    <>
                      {widgetElement}
                      {paragraph.content && (
                        <div className="mt-4 text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                      )}
                      {renderChildren()}
                    </>
                  );
                }
              })()}

              {/* Media Gallery Rendering with Layout */}
              {!paragraph.widget_id && paragraph.media_gallery_id && (() => {
                const galleryMedia = localGalleryMedia[paragraph.media_gallery_id] || [];
                const galleryLayout = galleryLayouts[paragraph.media_gallery_id];
                const mediaLayoutCode = paragraph.media_layout_code;

                // Render gallery with GalleryRenderer
                const galleryElement = (
                  <GalleryRenderer
                    media={galleryMedia}
                    layout={galleryLayout?.layout_code}
                    paragraphLayoutCode={mediaLayoutCode}
                    editMode={true}
                    onLayoutChange={(layoutCode) => onGalleryLayoutChange(paragraph.media_gallery_id, layoutCode)}
                    onAddMedia={(files) => onAddMediaToGallery(paragraph.media_gallery_id, files)}
                    onDeleteMedia={(mediaId) => onDeleteMediaFromGallery(paragraph.media_gallery_id, mediaId)}
                    onDeleteGallery={() => onRemoveGallery(paragraph.id)}
                    onReorderMedia={(mediaOrder) => onReorderMedia(paragraph.media_gallery_id, mediaOrder)}
                    onViewMedia={onViewMedia}
                    availableLayouts={onGetGalleryLayouts()}
                    uploading={uploadingGalleryId === paragraph.media_gallery_id}
                  />
                );

                // Render with layout
                if (mediaLayoutCode === 'SINGLE_CLEAR_LEFT') {
                  return (
                    <>
                      <div className="flex flex-col md:flex-row gap-4 mb-4">
                        <div className="md:w-1/3">{galleryElement}</div>
                        <div className="md:w-2/3">
                          {paragraph.content && (
                            <div className="text-secondary prose dark:prose-invert paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                          )}
                          {renderChildren()}
                        </div>
                      </div>
                    </>
                  );
                } else if (mediaLayoutCode === 'SINGLE_CLEAR_RIGHT') {
                  return (
                    <>
                      <div className="flex flex-col md:flex-row gap-4 mb-4">
                        <div className="md:w-2/3">
                          {paragraph.content && (
                            <div className="text-secondary prose dark:prose-invert paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                          )}
                          {renderChildren()}
                        </div>
                        <div className="md:w-1/3">{galleryElement}</div>
                      </div>
                    </>
                  );
                } else if (mediaLayoutCode === 'SINGLE_FLOAT_LEFT') {
                  return (
                    <>
                      <div className="float-left mr-4 mb-4 w-full md:w-1/3">{galleryElement}</div>
                      {paragraph.content && (
                        <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                      )}
                      {renderChildren()}
                      <div className="clear-both"></div>
                    </>
                  );
                } else if (mediaLayoutCode === 'SINGLE_FLOAT_RIGHT') {
                  return (
                    <>
                      <div className="float-right ml-4 mb-4 w-full md:w-1/3">{galleryElement}</div>
                      {paragraph.content && (
                        <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                      )}
                      {renderChildren()}
                      <div className="clear-both"></div>
                    </>
                  );
                } else if (mediaLayoutCode === 'SINGLE_ABOVE_PARAGRAPH') {
                  return (
                    <>
                      <div className="mb-4">{galleryElement}</div>
                      {paragraph.content && (
                        <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                      )}
                      {renderChildren()}
                    </>
                  );
                } else if (mediaLayoutCode === 'SINGLE_BELOW_PARAGRAPH') {
                  return (
                    <>
                      {paragraph.content && (
                        <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                      )}
                      {renderChildren()}
                      <div className="mt-4">{galleryElement}</div>
                    </>
                  );
                } else {
                  // No layout - show gallery above content + children
                  return (
                    <>
                      <div className="mb-4">{galleryElement}</div>
                      {paragraph.content && (
                        <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                      )}
                      {renderChildren()}
                    </>
                  );
                }
              })()}

              {/* Content only (no widget or media) */}
              {!paragraph.widget_id && !paragraph.media_gallery_id && (
                <>
                  {paragraph.content ? (
                    <div className="text-secondary prose dark:prose-invert max-w-none paragraph-content" dangerouslySetInnerHTML={{ __html: paragraph.content }} />
                  ) : (
                    <p className="text-gray-400 italic">Empty paragraph - click edit to add content</p>
                  )}
                  {renderChildren()}
                </>
              )}
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Paragraph Editor Component
const ParagraphEditor = ({ pageId, revisionId, onClose, onPublish, onDiscard, isDraft = false }) => {
  const { accessToken, profile } = useAuthStore();
  const {
    paragraphs,
    currentRevision,
    fetchParagraphs,
    createParagraph,
    updateParagraph,
    deleteParagraph,
    isLoading,
  } = usePagesStore();
  const { alert, confirm } = useDialogStore();
  const { widgets, fetchWidgets } = useWidgetsStore();
  const { mediaLayouts, widgetLayouts, fetchMediaLayouts, fetchWidgetLayouts } = useLayoutsStore();
  const {
    createGallery,
    deleteGallery,
    fetchGallery,
    addMedia,
    deleteMedia,
    updateGallery,
    reorderMedia
  } = useGalleriesStore();

  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [viewingMedia, setViewingMedia] = useState(null);
  const [uploadingGalleryId, setUploadingGalleryId] = useState(null);
  const [localGalleryMedia, setLocalGalleryMedia] = useState({});
  const [galleryLayouts, setGalleryLayouts] = useState({}); // Track layout for each gallery
  const [bulletinContexts, setBulletinContexts] = useState([]); // Bulletin contexts for NEWS_BULLETIN widget

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Fetch data on mount
  useEffect(() => {
    if (pageId && revisionId && accessToken) {
      fetchParagraphs(pageId, revisionId, accessToken);
    }
  }, [pageId, revisionId, accessToken, fetchParagraphs]);

  // Fetch bulletin contexts for NEWS_BULLETIN widget
  useEffect(() => {
    fetch('/api/lookup/bulletin-contexts')
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setBulletinContexts(data.data);
        }
      })
      .catch(err => console.error('Failed to fetch bulletin contexts:', err));
  }, []);

  // Load gallery media for all paragraphs (for view mode)
  useEffect(() => {
    const loadAllGalleries = async () => {
      if (!accessToken || paragraphs.length === 0) return;

      // Collect all gallery IDs from all paragraphs (including nested)
      const collectGalleryIds = (paragraphList) => {
        const ids = [];
        paragraphList.forEach(p => {
          if (p.media_gallery_id && !localGalleryMedia[p.media_gallery_id]) {
            ids.push(p.media_gallery_id);
          }
          if (p.children && p.children.length > 0) {
            ids.push(...collectGalleryIds(p.children));
          }
        });
        return ids;
      };

      const galleryIds = collectGalleryIds(paragraphs);

      // Load all galleries
      for (const galleryId of galleryIds) {
        const result = await fetchGallery(galleryId, accessToken);
        if (result.success && result.gallery) {
          setLocalGalleryMedia(prev => ({
            ...prev,
            [galleryId]: result.gallery.media || []
          }));
          setGalleryLayouts(prev => ({
            ...prev,
            [galleryId]: {
              layout_code: result.gallery.layout_code || null,
              layout_id: result.gallery.media_layout_id || null
            }
          }));
        }
      }
    };

    loadAllGalleries();
  }, [paragraphs, accessToken, fetchGallery]);

  // Load gallery media when paragraph with gallery is being edited
  useEffect(() => {
    const loadGalleryIfNeeded = async () => {
      const paragraph = paragraphs.find(p => p.id === editingId) ||
        paragraphs.flatMap(p => p.children || []).find(p => p.id === editingId);

      if (paragraph?.media_gallery_id && accessToken && !localGalleryMedia[paragraph.media_gallery_id]) {
        const result = await fetchGallery(paragraph.media_gallery_id, accessToken);
        if (result.success && result.gallery?.media) {
          setLocalGalleryMedia(prev => ({
            ...prev,
            [paragraph.media_gallery_id]: result.gallery.media
          }));
          // Always store gallery layout info (even if null) so we know it was loaded
          setGalleryLayouts(prev => ({
            ...prev,
            [paragraph.media_gallery_id]: {
              layout_code: result.gallery.layout_code || null,
              layout_id: result.gallery.media_layout_id || null
            }
          }));
        }
      }
    };

    loadGalleryIfNeeded();
  }, [editingId, paragraphs, accessToken]);

  useEffect(() => {
    if (accessToken) {
      fetchWidgets(accessToken);
      fetchMediaLayouts(accessToken);
      fetchWidgetLayouts(accessToken);
    }
  }, [accessToken, fetchWidgets, fetchMediaLayouts, fetchWidgetLayouts]);

  const handleEdit = (paragraph) => {
    setEditingId(paragraph.id);
    setEditValues({
      content: paragraph.content || '',
      widget_id: paragraph.widget_id || '',
      widget_layout_id: paragraph.widget_layout_id || '',
      media_gallery_id: paragraph.media_gallery_id || '',
      media_layout_id: paragraph.media_layout_id || '',
    });
  };

  const handleSave = async (paragraphId) => {
    // Enforce mutual exclusivity: if widget is set, clear media fields (and vice versa)
    const updates = { ...editValues };

    if (updates.widget_id) {
      // Widget is selected, clear media
      updates.media_gallery_id = null;
      updates.media_layout_id = null;
    }

    const result = await updateParagraph(paragraphId, updates, accessToken);

    if (result.success) {
      setEditingId(null);
      setEditValues({});
      // Refresh paragraphs
      await fetchParagraphs(pageId, revisionId, accessToken);
    } else {
      alert('Error', result.error || 'Failed to update paragraph');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleDelete = async (paragraphId) => {
    const confirmed = await confirm(
      'Delete Paragraph',
      'Are you sure you want to delete this paragraph? All child paragraphs will also be deleted.'
    );

    if (!confirmed) return;

    const result = await deleteParagraph(paragraphId, accessToken);

    if (result.success) {
      // Refresh paragraphs
      await fetchParagraphs(pageId, revisionId, accessToken);
    } else {
      alert('Error', result.error || 'Failed to delete paragraph');
    }
  };

  const handleAddRoot = async () => {
    const result = await createParagraph(
      {
        revision_id: revisionId,
        parent_paragraph_id: null,
        content: '<p>New paragraph</p>',
      },
      accessToken
    );

    if (result.success) {
      // Refresh paragraphs
      await fetchParagraphs(pageId, revisionId, accessToken);
    } else {
      alert('Error', result.error || 'Failed to create paragraph');
    }
  };

  const handleAddChild = async (parentId) => {
    const result = await createParagraph(
      {
        revision_id: revisionId,
        parent_paragraph_id: parentId,
        content: '<p>New child paragraph</p>',
      },
      accessToken
    );

    if (result.success) {
      // Refresh paragraphs
      await fetchParagraphs(pageId, revisionId, accessToken);
    } else {
      alert('Error', result.error || 'Failed to create child paragraph');
    }
  };

  const handleCreateGallery = async (paragraphId) => {
    if (!profile?.id) {
      alert('Error', 'Profile not found');
      return;
    }

    const result = await createGallery(
      {
        title: `Paragraph ${paragraphId} Gallery`,
        description: '',
        visibility_id: 2, // MEMBERS default
      },
      accessToken
    );

    if (result.success) {
      // Update paragraph with gallery ID and clear widget (mutually exclusive)
      await updateParagraph(
        paragraphId,
        {
          media_gallery_id: result.gallery.id,
          widget_id: null,
          widget_layout_id: null
        },
        accessToken
      );
      // Refresh paragraphs to show the gallery
      await fetchParagraphs(pageId, revisionId, accessToken);

      // Update editValues if this paragraph is being edited
      if (editingId === paragraphId) {
        setEditValues(prev => ({
          ...prev,
          media_gallery_id: result.gallery.id,
          widget_id: '',
          widget_layout_id: ''
        }));
      }
    } else {
      alert('Error', result.error || 'Failed to create gallery');
    }
  };

  const handleAddMediaToParagraphGallery = async (galleryId, files) => {
    if (!files || files.length === 0) return;

    setUploadingGalleryId(galleryId);

    const result = await addMedia(galleryId, files, accessToken);

    if (!result.success) {
      await alert('Error', result.error || 'Failed to upload media');
    } else {
      // Refresh gallery to show new media
      const galleryResult = await fetchGallery(galleryId, accessToken);
      if (galleryResult.success && galleryResult.gallery?.media) {
        setLocalGalleryMedia(prev => ({
          ...prev,
          [galleryId]: galleryResult.gallery.media
        }));
      }
    }

    setUploadingGalleryId(null);
  };

  const handleRemoveGallery = async (paragraphId) => {
    const confirmed = await confirm(
      'Remove Gallery',
      'Remove the media gallery from this paragraph? The gallery and its media will be deleted.'
    );

    if (!confirmed) return;

    // Get the paragraph to find the gallery ID
    const paragraph = paragraphs.find((p) => p.id === paragraphId) ||
      paragraphs.flatMap((p) => p.children || []).find((p) => p.id === paragraphId);

    if (paragraph?.media_gallery_id) {
      const galleryId = paragraph.media_gallery_id;

      // Delete the gallery
      await deleteGallery(galleryId, accessToken);

      // Remove from local state
      setLocalGalleryMedia(prev => {
        const newState = { ...prev };
        delete newState[galleryId];
        return newState;
      });

      // Update paragraph to remove gallery reference
      await updateParagraph(
        paragraphId,
        { media_gallery_id: null, media_layout_id: null },
        accessToken
      );

      // Update editValues immediately if this paragraph is being edited
      if (editingId === paragraphId) {
        setEditValues(prev => ({
          ...prev,
          media_gallery_id: '',
          media_layout_id: ''
        }));
      }

      // Refresh paragraphs to show updated state
      await fetchParagraphs(pageId, revisionId, accessToken);
    }
  };

  const handleRemoveWidget = async (paragraphId) => {
    const confirmed = await confirm(
      'Remove Widget',
      'Remove the widget from this paragraph?'
    );

    if (!confirmed) return;

    // Update paragraph to remove widget references
    await updateParagraph(
      paragraphId,
      { widget_id: null, widget_layout_id: null, widget_props: null, widget_maximizable: 0 },
      accessToken
    );

    // Update editValues immediately if this paragraph is being edited
    if (editingId === paragraphId) {
      setEditValues(prev => ({
        ...prev,
        widget_id: '',
        widget_layout_id: '',
        widget_props: null,
        widget_maximizable: false
      }));
    }

    // Refresh paragraphs to show updated state
    await fetchParagraphs(pageId, revisionId, accessToken);
  };

  const handleMediaLayoutChange = async (paragraphId, layoutId) => {
    const result = await updateParagraph(
      paragraphId,
      { media_layout_id: layoutId || null },
      accessToken
    );

    if (result.success) {
      await fetchParagraphs(pageId, revisionId, accessToken);
    }
  };

  const handleWidgetLayoutChange = async (paragraphId, layoutId) => {
    const result = await updateParagraph(
      paragraphId,
      { widget_layout_id: layoutId || null },
      accessToken
    );

    if (result.success) {
      await fetchParagraphs(pageId, revisionId, accessToken);
    }
  };

  const handleWidgetPropsChange = async (paragraphId, props) => {
    const result = await updateParagraph(
      paragraphId,
      { widget_props: JSON.stringify(props) },
      accessToken
    );

    if (result.success) {
      await fetchParagraphs(pageId, revisionId, accessToken);
    }
  };

  const handleAddWidget = async (paragraphId, widgetId) => {
    if (!widgetId) return;

    const result = await updateParagraph(
      paragraphId,
      { widget_id: widgetId },
      accessToken
    );

    if (result.success) {
      await fetchParagraphs(pageId, revisionId, accessToken);
    }
  };

  const flattenTree = (items) => {
    let flat = [];
    items.forEach((item) => {
      flat.push(item);
      if (item.children && item.children.length > 0) {
        flat = flat.concat(flattenTree(item.children));
      }
    });
    return flat;
  };

  // Callback functions to provide data to child components
  const getWidgets = () => widgets.filter(w => w.is_active);
  const getWidgetLayouts = () => widgetLayouts.filter(l => l.is_active);
  const getMediaLayouts = () => mediaLayouts.filter(l => l.is_active && l.type_id === 1); // Only SINGLE layouts for paragraph
  const getGalleryLayouts = () => mediaLayouts.filter(l => l.is_active && l.type_id === 2); // Only GALLERY layouts for gallery

  const handleAddMediaToGallery = async (galleryId, files) => {
    await handleAddMediaToParagraphGallery(galleryId, files);
  };

  const handleDeleteMediaFromGallery = async (galleryId, mediaId) => {
    const confirmed = await confirm(
      'Delete Media',
      'Are you sure you want to delete this media?'
    );
    if (!confirmed) return;

    // Close modal if this media is currently open
    if (viewingMedia?.id === mediaId) {
      setViewingMedia(null);
    }

    const result = await deleteMedia(galleryId, mediaId, accessToken);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to delete media');
    } else {
      // Refresh gallery
      const galleryResult = await fetchGallery(galleryId, accessToken);
      if (galleryResult.success && galleryResult.gallery?.media) {
        setLocalGalleryMedia(prev => ({
          ...prev,
          [galleryId]: galleryResult.gallery.media
        }));
      }
    }
  };

  const handleViewMedia = (media) => {
    setViewingMedia(media);
  };

  const handleGalleryLayoutChange = async (galleryId, layoutCode) => {
    console.log('ATTEMPTING TO UPDATE GALLERY LAYOUT:', { galleryId, layoutCode });
    const result = await updateGallery(galleryId, { layout_code: layoutCode }, accessToken);
    console.log('UPDATE GALLERY RESULT:', result);
    if (!result.success) {
      console.error('UPDATE FAILED:', result.error);
      await alert('Error', result.error || 'Failed to update gallery layout');
    } else {
      console.log('UPDATE SUCCEEDED, REFETCHING GALLERY...');
      // Refresh gallery data from backend to ensure we have the latest
      const galleryResult = await fetchGallery(galleryId, accessToken);
      console.log('REFETCHED GALLERY:', galleryResult);
      if (galleryResult.success && galleryResult.gallery) {
        // Update both media and layout
        if (galleryResult.gallery.media) {
          setLocalGalleryMedia(prev => ({
            ...prev,
            [galleryId]: galleryResult.gallery.media
          }));
        }
        setGalleryLayouts(prev => ({
          ...prev,
          [galleryId]: {
            layout_code: galleryResult.gallery.layout_code,
            layout_id: galleryResult.gallery.media_layout_id
          }
        }));
        console.log('FINAL GALLERY STATE:', {
          layout_code: galleryResult.gallery.layout_code,
          media_layout_id: galleryResult.gallery.media_layout_id
        });
      }
    }
  };

  const handleReorderMedia = async (galleryId, mediaOrder) => {
    console.log('🔄 Reordering media:', { galleryId, mediaOrder });
    const result = await reorderMedia(galleryId, mediaOrder, accessToken);
    console.log('🔄 Reorder result:', result);
    if (!result.success) {
      await alert('Error', result.error || 'Failed to reorder media');
      // Refresh gallery to revert optimistic update
      const galleryResult = await fetchGallery(galleryId, accessToken);
      if (galleryResult.success && galleryResult.gallery?.media) {
        setLocalGalleryMedia(prev => ({
          ...prev,
          [galleryId]: galleryResult.gallery.media
        }));
      }
    } else {
      console.log('✅ Media reordered successfully');
    }
  };

  if (isLoading && paragraphs.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="paragraph-editor">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h3 className="text-xl font-bold text-primary">
            Edit: {currentRevision?.title || 'Page Content'}
          </h3>
          {isDraft && (
            <span className="px-3 py-1 text-sm font-medium rounded bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300">
              DRAFT
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {isDraft && onPublish && (
            <button
              onClick={onPublish}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              title="Publish this draft"
            >
              Publish
            </button>
          )}
          {isDraft && onDiscard && (
            <button
              onClick={onDiscard}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              title="Discard this draft"
            >
              Discard
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-secondary hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter}>
        <SortableContext items={flattenTree(paragraphs)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {paragraphs.map((paragraph) => (
              <SortableParagraph
                key={paragraph.id}
                paragraph={paragraph}
                depth={0}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onAddChild={handleAddChild}
                editingId={editingId}
                editValues={editValues}
                setEditValues={setEditValues}
                onSave={handleSave}
                onCancelEdit={handleCancelEdit}
                onGetWidgets={getWidgets}
                onGetWidgetLayouts={getWidgetLayouts}
                onGetMediaLayouts={getMediaLayouts}
                onGetGalleryLayouts={getGalleryLayouts}
                onCreateGallery={handleCreateGallery}
                onRemoveGallery={handleRemoveGallery}
                onRemoveWidget={handleRemoveWidget}
                onAddWidget={handleAddWidget}
                onMediaLayoutChange={handleMediaLayoutChange}
                onWidgetLayoutChange={handleWidgetLayoutChange}
                onWidgetPropsChange={handleWidgetPropsChange}
                onAddMediaToGallery={handleAddMediaToGallery}
                onDeleteMediaFromGallery={handleDeleteMediaFromGallery}
                onReorderMedia={handleReorderMedia}
                onViewMedia={handleViewMedia}
                onGalleryLayoutChange={handleGalleryLayoutChange}
                localGalleryMedia={localGalleryMedia}
                galleryLayouts={galleryLayouts}
                setGalleryLayouts={setGalleryLayouts}
                uploadingGalleryId={uploadingGalleryId}
                profile={profile}
                bulletinContexts={bulletinContexts}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {paragraphs.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-secondary mb-4">No paragraphs yet</p>
          <button
            onClick={handleAddRoot}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
          >
            + Add First Paragraph
          </button>
        </div>
      ) : (
        <div className="flex justify-center mt-6">
          <button
            onClick={handleAddRoot}
            className="w-12 h-12 rounded-full bg-primary-600 text-white hover:bg-primary-700 flex items-center justify-center text-2xl shadow-lg transition-all hover:scale-110"
            title="Add paragraph"
          >
            +
          </button>
        </div>
      )}

      {/* View Media Modal */}
      {viewingMedia && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60]"
          onClick={() => setViewingMedia(null)}
        >
          <button
            onClick={() => setViewingMedia(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="max-w-6xl max-h-[90vh] p-4" onClick={(e) => e.stopPropagation()}>
            {viewingMedia.kind_code === 'IMAGE' ? (
              <ProtectedImage
                uuid={viewingMedia.uuid}
                alt={viewingMedia.filename}
                className="max-w-full max-h-[90vh] object-contain"
              />
            ) : (
              <ProtectedVideo
                uuid={viewingMedia.uuid}
                controls={true}
                autoPlay={true}
                className="max-w-full max-h-[90vh]"
              />
            )}
          </div>
        </div>
      )}

      {/* CSS for proper list display */}
      <style>{`
        .paragraph-content ul,
        .paragraph-content ol {
          list-style-position: inside;
          margin: 1em 0;
          padding-left: 1.5em;
        }
        .paragraph-content ul {
          list-style-type: disc;
        }
        .paragraph-content ol {
          list-style-type: decimal;
        }
        .paragraph-content ul ul {
          list-style-type: circle;
        }
        .paragraph-content ul ul ul {
          list-style-type: square;
        }
        .paragraph-content li {
          margin: 0.5em 0;
          display: list-item;
        }

        /* Todo list styles */
        .paragraph-content ul.todo-list {
          list-style: none;
          padding-left: 0;
        }
        .paragraph-content ul.todo-list li {
          display: flex;
          align-items: flex-start;
          gap: 0.5em;
        }
        .paragraph-content ul.todo-list input[type="checkbox"] {
          margin-top: 0.25em;
          cursor: default;
          pointer-events: none;
        }
        .paragraph-content ul.todo-list li span {
          flex: 1;
        }

        /* Headings from rich text */
        .paragraph-content h1 {
          font-size: 2em;
          font-weight: bold;
          margin: 0.67em 0;
        }
        .paragraph-content h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin: 0.75em 0;
        }
        .paragraph-content h3 {
          font-size: 1.17em;
          font-weight: bold;
          margin: 0.83em 0;
        }
        .paragraph-content h4 {
          font-size: 1em;
          font-weight: bold;
          margin: 1em 0;
        }

        /* Links */
        .paragraph-content a {
          color: #3b82f6;
          text-decoration: underline;
          cursor: pointer;
        }
        .paragraph-content a:hover {
          color: #2563eb;
        }
      `}</style>
    </div>
  );
};

export default ParagraphEditor;
